// JSON-LD Structured Data for SEO

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ChopperSwap',
  description: 'The Professional Helicopter Exchange - Premier marketplace for buying, selling, and renting helicopters, engines, parts, aviation jobs, and services worldwide.',
  url: 'https://chopperswap.com',
  logo: 'https://chopperswap.com/logo.png',
  sameAs: [
    'https://twitter.com/chopperswap',
    'https://www.linkedin.com/company/chopperswap',
    'https://www.facebook.com/chopperswap',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['English'],
  },
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'ChopperSwap',
  url: 'https://chopperswap.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://chopperswap.com/marketplace?search={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://www.chopperswap.com${item.url}`,
  })),
});

type AggregateRating = { ratingValue: number; reviewCount: number };
type Availability = 'InStock' | 'OutOfStock' | 'Discontinued' | 'PreOrder' | 'SoldOut';

const availabilityUrl = (a?: Availability) =>
  `https://schema.org/${a ?? 'InStock'}`;

export const helicopterListingSchema = (listing: {
  title: string;
  description: string;
  price?: number;
  currency?: string;
  images?: string[];
  manufacturer?: string;
  model?: string;
  year?: number;
  condition?: string;
  location?: string;
  url?: string;
  priceValidUntil?: string;
  availability?: Availability;
  aggregateRating?: AggregateRating;
  sellerName?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: listing.title,
  description: listing.description,
  image: listing.images?.[0],
  brand: listing.manufacturer ? {
    '@type': 'Brand',
    name: listing.manufacturer,
  } : undefined,
  model: listing.model,
  productionDate: listing.year?.toString(),
  url: listing.url,
  itemCondition: listing.condition === 'new'
    ? 'https://schema.org/NewCondition'
    : 'https://schema.org/UsedCondition',
  offers: listing.price ? {
    '@type': 'Offer',
    price: listing.price,
    priceCurrency: listing.currency || 'USD',
    availability: availabilityUrl(listing.availability),
    priceValidUntil: listing.priceValidUntil,
    seller: {
      '@type': 'Organization',
      name: listing.sellerName || 'ChopperSwap',
    },
  } : undefined,
  aggregateRating: listing.aggregateRating ? {
    '@type': 'AggregateRating',
    ratingValue: listing.aggregateRating.ratingValue,
    reviewCount: listing.aggregateRating.reviewCount,
  } : undefined,
  areaServed: listing.location,
});

export const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

// Expanded pricing FAQs for all 5 asset types (for structured data)
export const pricingFaqs = [
  // Core Platform
  {
    question: 'How much does it cost to list a helicopter on ChopperSwap?',
    answer: 'Helicopter listings start at just $39 for a 30-day Standard listing, or $69 for a 60-day Featured listing with premium placement. For high-volume dealers, Helicopter Pro is available at $899/year for unlimited featured listings.',
  },
  {
    question: 'Are buy requests free on ChopperSwap?',
    answer: 'Yes! All buy requests for helicopters, engines, and parts are completely FREE forever. Only sellers pay listing fees.',
  },
  {
    question: 'How does ChopperSwap compare to other aviation marketplaces?',
    answer: 'ChopperSwap is up to 97% more affordable than enterprise competitors like PartsBase or JETNET, while offering modern features like instant sharing, secure document vault, mobile optimization, and 5 integrated marketplaces (Helicopters, Engines, Parts, Jobs, Services).',
  },
  {
    question: 'Can I list engines and parts on ChopperSwap?',
    answer: 'Absolutely! ChopperSwap supports five asset categories: Helicopters, Turbine Engines, Parts & Tools, Aviation Jobs, and Services. Each has tailored listing options and competitive pricing.',
  },
  // Jobs
  {
    question: 'Is it free to create a professional profile on ChopperSwap?',
    answer: 'Yes! Professional profiles for pilots, mechanics, and other aviation specialists are completely FREE forever. Only employers posting job listings pay fees. Showcase your type ratings, licenses, and certifications at no cost.',
  },
  {
    question: 'What job categories does ChopperSwap support?',
    answer: 'ChopperSwap supports 12 aviation job categories: Pilots, Mechanics & Maintenance, CAMO, Part-21, Cabin Crew, Ground Operations, ATC & Dispatch, Training & Education, Drones & UAS, Airport Operations, Engineering & Technical, and Management & Administration.',
  },
  {
    question: 'How much does it cost to post a job on ChopperSwap?',
    answer: 'Job listings start at $29 for a 30-day Standard posting, or $49 for a 60-day Featured listing with social boost. Jobs Pro is available at $499/year for unlimited featured job posts.',
  },
  // Services
  {
    question: 'What types of services can I list on ChopperSwap?',
    answer: 'ChopperSwap supports 10 service categories: MRO (Part-145), CAMO, Part-21 (DOA/POA), Training & Education, Operator Services, Engineering, Ground Operations, FBO/Heliport, Management, and Drone/UAS services.',
  },
  {
    question: 'Are service requests and RFQs free on ChopperSwap?',
    answer: 'Yes! Posting service requests and RFQs is completely FREE forever. Only service providers pay listing fees. Request quotes from multiple providers at no cost.',
  },
  {
    question: 'How much does it cost to list a service on ChopperSwap?',
    answer: 'Service listings start at $19 for a 30-day Standard listing, or $39 for a 60-day Featured listing. Services Pro is available at $399/year for unlimited featured listings with RFQ priority access.',
  },
  // General
  {
    question: 'Does ChopperSwap support AOG services?',
    answer: 'Yes! Service providers can indicate 24/7 AOG support availability, response times, and shift coverage. Clients can filter specifically for AOG-capable providers when they need urgent assistance.',
  },
  {
    question: 'Are there any hidden fees on ChopperSwap?',
    answer: 'Zero hidden fees. The price you see is the price you pay. No transaction fees, no success fees, no surprise charges. Ever. This applies to all 5 asset types.',
  },
];

// Company profile schema for SEO
export const companyProfileSchema = (company: {
  name: string;
  description?: string;
  logo?: string;
  website?: string;
  email?: string;
  phone?: string;
  location?: string;
  certifications?: string[];
  foundedYear?: number;
  employeesRange?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: company.name,
  description: company.description,
  logo: company.logo,
  url: company.website,
  email: company.email,
  telephone: company.phone,
  foundingDate: company.foundedYear?.toString(),
  numberOfEmployees: company.employeesRange ? {
    '@type': 'QuantitativeValue',
    value: company.employeesRange,
  } : undefined,
  address: company.location ? {
    '@type': 'PostalAddress',
    addressLocality: company.location,
  } : undefined,
  hasCredential: company.certifications?.map(cert => ({
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: 'certification',
    name: cert,
  })),
  areaServed: {
    '@type': 'Place',
    name: 'Worldwide',
  },
  memberOf: {
    '@type': 'Organization',
    name: 'ChopperSwap Aviation Marketplace',
    url: 'https://chopperswap.com',
  },
});

// Seller profile schema
export const sellerProfileSchema = (seller: {
  name: string;
  url: string;
  image?: string;
  description?: string;
  location?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: seller.name,
  url: seller.url,
  image: seller.image,
  description: seller.description,
  address: seller.location ? {
    '@type': 'PostalAddress',
    addressLocality: seller.location,
  } : undefined,
});

// ===== Asset-specific Product / JobPosting / Service schemas =====

export const engineListingSchema = (listing: {
  title: string;
  description: string;
  price?: number;
  currency?: string;
  images?: string[];
  manufacturer?: string;
  model?: string;
  condition?: string;
  location?: string;
  url?: string;
  priceValidUntil?: string;
  availability?: Availability;
  aggregateRating?: AggregateRating;
  sellerName?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  category: 'Aircraft Turbine Engine',
  name: listing.title,
  description: listing.description,
  image: listing.images?.[0],
  brand: listing.manufacturer ? { '@type': 'Brand', name: listing.manufacturer } : undefined,
  model: listing.model,
  url: listing.url,
  itemCondition:
    listing.condition === 'new'
      ? 'https://schema.org/NewCondition'
      : 'https://schema.org/UsedCondition',
  offers: listing.price
    ? {
        '@type': 'Offer',
        price: listing.price,
        priceCurrency: listing.currency || 'USD',
        availability: availabilityUrl(listing.availability),
        priceValidUntil: listing.priceValidUntil,
        seller: { '@type': 'Organization', name: listing.sellerName || 'ChopperSwap' },
      }
    : undefined,
  aggregateRating: listing.aggregateRating ? {
    '@type': 'AggregateRating',
    ratingValue: listing.aggregateRating.ratingValue,
    reviewCount: listing.aggregateRating.reviewCount,
  } : undefined,
  areaServed: listing.location,
});

export const partsListingSchema = (listing: {
  title: string;
  description: string;
  price?: number;
  currency?: string;
  images?: string[];
  manufacturer?: string;
  partNumber?: string;
  condition?: string;
  location?: string;
  url?: string;
  priceValidUntil?: string;
  availability?: Availability;
  aggregateRating?: AggregateRating;
  sellerName?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  category: 'Aircraft Parts',
  name: listing.title,
  description: listing.description,
  image: listing.images?.[0],
  brand: listing.manufacturer ? { '@type': 'Brand', name: listing.manufacturer } : undefined,
  sku: listing.partNumber,
  mpn: listing.partNumber,
  url: listing.url,
  itemCondition:
    listing.condition === 'new'
      ? 'https://schema.org/NewCondition'
      : 'https://schema.org/UsedCondition',
  offers: listing.price
    ? {
        '@type': 'Offer',
        price: listing.price,
        priceCurrency: listing.currency || 'USD',
        availability: availabilityUrl(listing.availability),
        priceValidUntil: listing.priceValidUntil,
        seller: { '@type': 'Organization', name: listing.sellerName || 'ChopperSwap' },
      }
    : undefined,
  aggregateRating: listing.aggregateRating ? {
    '@type': 'AggregateRating',
    ratingValue: listing.aggregateRating.ratingValue,
    reviewCount: listing.aggregateRating.reviewCount,
  } : undefined,
  areaServed: listing.location,
});

export const jobPostingSchema = (job: {
  title: string;
  description: string;
  datePosted?: string;
  validThrough?: string;
  employmentType?: string;
  location?: string;
  country?: string;
  hiringOrganization?: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  url?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'JobPosting',
  title: job.title,
  description: job.description,
  datePosted: job.datePosted,
  validThrough: job.validThrough,
  employmentType: job.employmentType,
  url: job.url,
  hiringOrganization: {
    '@type': 'Organization',
    name: job.hiringOrganization || 'ChopperSwap Employer',
    sameAs: 'https://chopperswap.com',
  },
  jobLocation: job.location
    ? {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: job.location,
          addressCountry: job.country,
        },
      }
    : undefined,
  baseSalary:
    job.salaryMin || job.salaryMax
      ? {
          '@type': 'MonetaryAmount',
          currency: job.salaryCurrency || 'USD',
          value: {
            '@type': 'QuantitativeValue',
            minValue: job.salaryMin,
            maxValue: job.salaryMax,
            unitText: 'YEAR',
          },
        }
      : undefined,
});

export const serviceListingSchema = (svc: {
  title: string;
  description: string;
  provider?: string;
  serviceType?: string;
  areaServed?: string;
  price?: number;
  currency?: string;
  url?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: svc.title,
  description: svc.description,
  serviceType: svc.serviceType,
  url: svc.url,
  provider: {
    '@type': 'Organization',
    name: svc.provider || 'ChopperSwap Service Provider',
  },
  areaServed: svc.areaServed
    ? { '@type': 'Place', name: svc.areaServed }
    : { '@type': 'Place', name: 'Worldwide' },
  offers: svc.price
    ? {
        '@type': 'Offer',
        price: svc.price,
        priceCurrency: svc.currency || 'USD',
      }
    : undefined,
});

// ItemList schema for category landing pages — wraps an array of listings
export const itemListSchema = (items: { name: string; url: string; image?: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  numberOfItems: items.length,
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    url: item.url.startsWith('http') ? item.url : `https://www.chopperswap.com${item.url}`,
    image: item.image,
  })),
});

// ===== Week 11 schema enrichment =====

// LocalBusiness schema for company / service-provider pages with a physical
// presence. Use when the entity has an address and serves customers locally.
export const localBusinessSchema = (biz: {
  name: string;
  description?: string;
  url?: string;
  logo?: string;
  image?: string;
  telephone?: string;
  email?: string;
  streetAddress?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  priceRange?: string;
  openingHours?: string[];
  areaServed?: string;
  aggregateRating?: AggregateRating;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: biz.name,
  description: biz.description,
  url: biz.url,
  logo: biz.logo,
  image: biz.image || biz.logo,
  telephone: biz.telephone,
  email: biz.email,
  priceRange: biz.priceRange,
  openingHours: biz.openingHours,
  address: (biz.streetAddress || biz.city || biz.country) ? {
    '@type': 'PostalAddress',
    streetAddress: biz.streetAddress,
    addressLocality: biz.city,
    addressRegion: biz.region,
    postalCode: biz.postalCode,
    addressCountry: biz.country,
  } : undefined,
  geo: (biz.latitude != null && biz.longitude != null) ? {
    '@type': 'GeoCoordinates',
    latitude: biz.latitude,
    longitude: biz.longitude,
  } : undefined,
  areaServed: biz.areaServed ? { '@type': 'Place', name: biz.areaServed } : undefined,
  aggregateRating: biz.aggregateRating ? {
    '@type': 'AggregateRating',
    ratingValue: biz.aggregateRating.ratingValue,
    reviewCount: biz.aggregateRating.reviewCount,
  } : undefined,
});

// Individual review schema — attach to a Product/Service when reviews exist.
export const reviewSchema = (review: {
  author: string;
  ratingValue: number;
  body?: string;
  datePublished?: string;
  itemName?: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Review',
  author: { '@type': 'Person', name: review.author },
  reviewRating: {
    '@type': 'Rating',
    ratingValue: review.ratingValue,
    bestRating: 5,
  },
  reviewBody: review.body,
  datePublished: review.datePublished,
  itemReviewed: review.itemName ? { '@type': 'Thing', name: review.itemName } : undefined,
});

// Helper to inject JSON-LD into head
export const getJsonLdScript = (data: object) => {
  return JSON.stringify(data);
};
